import React, {type ComponentType, memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {FormattedMessage} from 'react-intl';
import type {FixedSizeList, ListChildComponentProps} from 'react-window';
import type InfiniteLoader from 'react-window-infinite-loader';
import {debounce} from 'lodash';

import {useEventCallback} from 'usehooks-ts';

import {type Emoji, type EmojiCategory} from 'mattermost-redux/types/emojis';
import {isSystemEmoji} from 'mattermost-redux/utils/emoji_utils';

import {NoResultsVariant} from 'components/no_results_indicator/types';
import {
    type Categories,
    type CategoryOrEmojiRow,
    type EmojiCursor,
    type EmojiPosition,
    type EmojiRow,
    NavigationDirection,
} from 'components/emoji_picker/types';
import {
    CATEGORIES,
    EMOJI_PER_ROW,
    EMOJIS_ROW,
    RECENT,
    RECENT_EMOJI_CATEGORY,
    SEARCH_RESULTS,
    SMILEY_EMOTION,
} from 'components/emoji_picker/constants';
import {
    createCategoryAndEmojiRows,
    getCursorProperties,
    getUpdatedCategoriesAndAllEmojis,
} from 'components/emoji_picker/utils';
import NoResultsIndicator from 'components/no_results_indicator';
import EmojiPickerPreview from 'components/emoji_picker/components/emoji_picker_preview';
import EmojiPickerSearch from 'components/emoji_picker/components/emoji_picker_search';
import EmojiPickerSkin from 'components/emoji_picker/components/emoji_picker_skin';
import EmojiPickerCategories from 'components/emoji_picker/components/emoji_picker_categories';
import EmojiPickerCustomEmojiButton from 'components/emoji_picker/components/emoji_picker_custom_emoji_button';
import EmojiPickerCurrentResults from 'components/emoji_picker/components/emoji_picker_current_results';

import {SearchTimeMeasurer} from '@time-webkit/statist';

import Constants from 'utils/constants';

import EmojiPickerCategoryOrEmojiRow from './components/emoji_picker_category_or_emoji_row';

import {CursorContext} from './cursor_context';

import type {PropsFromRedux} from './index';

interface Props extends PropsFromRedux {
    filter: string;
    onEmojiClick: (emoji: Emoji) => void;
    handleFilterChange: (filter: string) => void;
}

const searchTimeMeasurer = new SearchTimeMeasurer('searchEmoji.result');

const EmojiPicker = ({
    filter,
    onEmojiClick,
    handleFilterChange,
    customEmojisEnabled = false,
    customEmojiPage = 0,
    emojiMap,
    recentEmojis,
    userSkinTone,
    currentTeamName,
    actions: {
        getCustomEmojis,
        searchCustomEmojis,
        incrementEmojiPickerPage,
        setUserSkinTone,
    },
}: Props) => {
    const getInitialActiveCategory = () => (recentEmojis.length ? RECENT : SMILEY_EMOTION);
    const [activeCategory, setActiveCategory] = useState<EmojiCategory>(getInitialActiveCategory);

    const [cursor, setCursor] = useState<EmojiCursor>({
        rowIndex: -1,
        emojiId: '',
        emoji: undefined,
    });

    // On the first load, categories doesnt contain emojiIds until later when getUpdatedCategoriesAndAllEmojis is called
    const getInitialCategories = () => (recentEmojis.length ? {...RECENT_EMOJI_CATEGORY, ...CATEGORIES} : CATEGORIES);
    const [categories, setCategories] = useState<Categories>(getInitialCategories);

    const [allEmojis, setAllEmojis] = useState<Record<string, Emoji>>({});

    const [categoryOrEmojisRows, setCategoryOrEmojisRows] = useState<CategoryOrEmojiRow[]>([]);

    // contains all emojis visible on screen sorted by category
    const [emojiPositions, setEmojiPositionsArray] = useState<EmojiPosition[]>([]);

    const searchInputRef = useRef<HTMLInputElement>(null);

    const infiniteLoaderRef = React.useRef<InfiniteLoader & {_listRef: FixedSizeList<CategoryOrEmojiRow[]>}>(null);

    const shouldRunCreateCategoryAndEmojiRows = useRef<boolean>();

    const debouncedSearchCustomEmoji = useMemo(() => {
        return debounce((newFilter, customEmojisEnabled, signal) => {
            if (customEmojisEnabled && newFilter && newFilter.trim().length) {
                const date = Date.now();

                searchCustomEmojis(newFilter, {signal}).then(({data, error}) => {
                    if (!error && data?.length) {
                        searchTimeMeasurer.fromDate(newFilter, date);
                    }
                });
            }
        }, Constants.SEARCH_TIMEOUT_MILLISECONDS);

        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        // Delay taking focus because this briefly renders offscreen when using an Overlay
        // so focusing it immediately on mount can cause weird scrolling
        const searchFocusAnimationFrame = window.requestAnimationFrame(() => {
            searchInputRef.current?.focus();
        });

        const rootComponent = document.getElementById('root');
        rootComponent?.classList.add('emoji-picker--active');

        return () => {
            rootComponent?.classList.remove('emoji-picker--active');
            window.cancelAnimationFrame(searchFocusAnimationFrame);
        };
    }, []);

    useEffect(() => {
        shouldRunCreateCategoryAndEmojiRows.current = true;

        const [updatedCategories, updatedAllEmojis] = getUpdatedCategoriesAndAllEmojis(emojiMap, recentEmojis, userSkinTone, allEmojis);
        setAllEmojis(updatedAllEmojis);
        setCategories(updatedCategories);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emojiMap, userSkinTone, recentEmojis]);

    useEffect(() => {
        shouldRunCreateCategoryAndEmojiRows.current = false;

        const controller = new AbortController();
        const [updatedCategoryOrEmojisRows, updatedEmojiPositions] = createCategoryAndEmojiRows(allEmojis, categories, filter, userSkinTone);

        setCategoryOrEmojisRows(updatedCategoryOrEmojisRows);
        setEmojiPositionsArray(updatedEmojiPositions);
        debouncedSearchCustomEmoji(filter, customEmojisEnabled, controller.signal);

        return () => {
            controller.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, userSkinTone, shouldRunCreateCategoryAndEmojiRows.current, customEmojisEnabled]);

    useEffect(() => {
        searchTimeMeasurer.measure(filter);
    }, [filter]);

    useEffect(() => {
        /**
         * Если categoryOrEmojisRows пуст, то окно поиска еще не проинициализированно
         * начальными результатами (списком эмоджей из стора).
         * Для пустой выдачи categoryOrEmojisRows будет содержать один элемент для экрана "Нет результатов"
         */
        if (!categoryOrEmojisRows.length) {
            return;
        }

        const resultsCount = categoryOrEmojisRows.reduce((prev, cur) => {
            return prev + (cur.type === EMOJIS_ROW ? cur.items.length : 0);
        }, 0);
        searchTimeMeasurer.measure(filter, resultsCount);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryOrEmojisRows]);

    // clear out the active category on search input
    useEffect(() => {
        if (activeCategory !== getInitialActiveCategory()) {
            setActiveCategory(getInitialActiveCategory());
        }

        // eslint-disable-next-line no-underscore-dangle
        infiniteLoaderRef?.current?._listRef?.scrollToItem(0, 'start');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    // scroll as little as possible on cursor navigation
    useEffect(() => {
        if (cursor.emojiId) {
            // eslint-disable-next-line no-underscore-dangle
            infiniteLoaderRef?.current?._listRef?.scrollToItem(cursor.rowIndex, 'auto');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cursor.rowIndex]);

    const focusOnSearchInput = useCallback(() => {
        searchInputRef.current?.focus();
    }, []);

    const getEmojiById = (emojiId: string) => {
        if (!emojiId) {
            return null;
        }
        const emoji = allEmojis[emojiId] || allEmojis[emojiId.toUpperCase()] || allEmojis[emojiId.toLowerCase()];
        return emoji;
    };

    const handleCategoryClick = useCallback((categoryRowIndex: CategoryOrEmojiRow['index'], categoryName: EmojiCategory, emojiId: string) => {
        if (!categoryName || categoryName === activeCategory || !emojiId) {
            return;
        }

        setActiveCategory(categoryName);

        // eslint-disable-next-line no-underscore-dangle
        infiniteLoaderRef?.current?._listRef?.scrollToItem(categoryRowIndex, 'start');

        const cursorEmoji = getEmojiById(emojiId);
        if (cursorEmoji) {
            setCursor({
                rowIndex: categoryRowIndex + 1, // +1 because next row is the emoji row
                emojiId,
                emoji: cursorEmoji,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeCategory]);

    const resetCursor = useCallback(() => {
        setCursor({
            rowIndex: -1,
            emojiId: '',
            emoji: undefined,
        });
    }, []);

    const [cursorCategory, cursorCategoryIndex, cursorEmojiIndex] = getCursorProperties(cursor.rowIndex, cursor.emojiId, categoryOrEmojisRows as EmojiRow[]);

    function calculateNewCursorForUpArrow(cursorCategory: string, emojiPositions: EmojiPosition[], currentCursorsPositionIndex: number, categories: Categories, focusOnSearchInput: () => void) {
        if ((currentCursorsPositionIndex - EMOJI_PER_ROW) >= 0) {
            // Emoji is present up a row in same x position
            const upTheRowCategoryName = emojiPositions[currentCursorsPositionIndex - EMOJI_PER_ROW].categoryName as EmojiCategory;

            if (upTheRowCategoryName !== cursorCategory) {
                // If up the row emoji is in different category, move to that category's last emoji
                const upTheRowCategorysEmojis = categories[upTheRowCategoryName].emojiIds || [];
                const lastEmojiIdUpTheRow = upTheRowCategorysEmojis[upTheRowCategorysEmojis.length - 1];
                const lastEmojiPositionUpTheRow = emojiPositions.find((emojiPosition) => {
                    return emojiPosition.emojiId.toLowerCase() === lastEmojiIdUpTheRow.toLowerCase() && emojiPosition.categoryName === upTheRowCategoryName;
                });
                return lastEmojiPositionUpTheRow;
            }

            // If up the row emoji is in same category, move up in same category
            return emojiPositions[currentCursorsPositionIndex - EMOJI_PER_ROW];
        }

        // When emojis in the assumingly top row are less than EMOJI_PER_ROW,
        // Check if those are of different category
        const startingEmojisOfDifferentCategory = emojiPositions.slice(0, currentCursorsPositionIndex).reverse().find((emojiPosition) => {
            return emojiPosition.categoryName !== cursorCategory;
        });

        if (startingEmojisOfDifferentCategory) {
            return startingEmojisOfDifferentCategory;
        }

        // We are already at the first row, so focus on search
        focusOnSearchInput();
        return undefined;
    }

    function calculateNewCursorForRightOrLeftArrow(moveTo: NavigationDirection, emojiPositions: EmojiPosition[], currentCursorIndexInEmojis: number, focusOnSearchInput: () => void) {
        if (moveTo === NavigationDirection.NextEmoji && ((currentCursorIndexInEmojis + 1) < emojiPositions.length)) {
            // When next emoji is present, move to next emoji
            return emojiPositions[currentCursorIndexInEmojis + 1];
        }
        if (moveTo === NavigationDirection.PreviousEmoji && ((currentCursorIndexInEmojis - 1) >= 0)) {
            // When previous emoji is present, move to previous emoji
            return emojiPositions[currentCursorIndexInEmojis - 1];
        }
        if (moveTo === NavigationDirection.PreviousEmoji && ((currentCursorIndexInEmojis - 1) < 0)) {
            // If cursor was at first emoji then focus on search input
            focusOnSearchInput();
            return undefined;
        }

        return undefined;
    }

    function calculateNewCursorForDownArrow(cursorCategory: string, emojiPositions: EmojiPosition[], currentCursorsPositionIndex: number, categories: Categories) {
        if ((currentCursorsPositionIndex + EMOJI_PER_ROW) < emojiPositions.length) {
            // Emoji is present down a row in same x position
            const downTheRowCategoryName = emojiPositions[currentCursorsPositionIndex + EMOJI_PER_ROW].categoryName as EmojiCategory;

            if (downTheRowCategoryName !== cursorCategory) {
                // If down the row emoji is in different category, move to that category's first emoji
                const downTheRowCategorysEmojis = categories[downTheRowCategoryName].emojiIds || [];
                const firstEmojiIdDownTheRow = downTheRowCategorysEmojis[0];
                const firstEmojiPositionDownTheRow = emojiPositions.find((emojiPosition) => {
                    return emojiPosition.emojiId.toLowerCase() === firstEmojiIdDownTheRow.toLowerCase() && emojiPosition.categoryName === downTheRowCategoryName;
                });
                return firstEmojiPositionDownTheRow;
            }

            // If down the row emoji is in same category, move to down in same category
            return emojiPositions[currentCursorsPositionIndex + EMOJI_PER_ROW];
        }

        // When emoji down the row is not present.
        // Check if the remaining emojis are of different category
        const endingEmojisOfDifferentCategory = emojiPositions.slice(currentCursorsPositionIndex + 1, emojiPositions.length).find((emojiPosition) => {
            return emojiPosition.categoryName !== cursorCategory;
        });

        if (endingEmojisOfDifferentCategory) {
            return endingEmojisOfDifferentCategory;
        }

        return undefined;
    }

    const handleKeyboardEmojiNavigation = (moveTo: NavigationDirection) => {
        // No navigateable emoji are present in the resutls
        if (emojiPositions.length === 0) {
            return;
        }

        let newCursor;
        if (cursor.emojiId.length !== 0 && cursor.rowIndex !== -1) {
            // If cursor is on an emoji
            const currentCursorsPositionIndex = emojiPositions.findIndex((emojiPosition) =>
                emojiPosition.rowIndex === cursor.rowIndex && emojiPosition.emojiId.toLowerCase() === cursor.emojiId.toLowerCase());

            if (currentCursorsPositionIndex === -1) {
                newCursor = undefined;
            } else if (moveTo === NavigationDirection.NextEmoji || moveTo === NavigationDirection.PreviousEmoji) {
                newCursor = calculateNewCursorForRightOrLeftArrow(moveTo, emojiPositions, currentCursorsPositionIndex, focusOnSearchInput);
            } else if (moveTo === NavigationDirection.NextEmojiRow) {
                newCursor = calculateNewCursorForDownArrow(cursorCategory, emojiPositions, currentCursorsPositionIndex, categories);
            } else if (moveTo === NavigationDirection.PreviousEmojiRow) {
                newCursor = calculateNewCursorForUpArrow(cursorCategory, emojiPositions, currentCursorsPositionIndex, categories, focusOnSearchInput);
            }
        } else if (cursor.emojiId.length === 0 && cursor.rowIndex === -1) {
            if (moveTo === NavigationDirection.NextEmoji || moveTo === NavigationDirection.NextEmojiRow) {
                // if no cursor is selected, set the first emoji on arrows right & down
                if (emojiPositions.length !== 0) {
                    newCursor = emojiPositions[0];
                }
            }
        }

        // If newCursorIndex is less than 0, abort and do nothing
        if (newCursor === undefined) {
            return;
        }

        const newCursorEmoji = getEmojiById(newCursor.emojiId);
        if (!newCursorEmoji) {
            return;
        }

        setCursor({
            rowIndex: newCursor.rowIndex,
            emojiId: newCursor.emojiId,
            emoji: newCursorEmoji,
        });
    };

    const handleEnterOnEmoji = useCallback(() => {
        const clickedEmoji = cursor.emoji;

        if (clickedEmoji) {
            onEmojiClick(clickedEmoji);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cursor.emojiId]);

    const handleEmojiOnMouseOver = useEventCallback((mouseOverCursor: EmojiCursor) => {
        if (mouseOverCursor.emojiId !== cursor.emojiId || cursor.emojiId === '') {
            setCursor(mouseOverCursor);
        }
    });

    const handleEmojiClick = useEventCallback(onEmojiClick);

    const cursorEmojiName = useMemo(() => {
        const {emoji} = cursor;

        if (!emoji) {
            return '';
        }

        const name = isSystemEmoji(emoji) ? emoji.short_name : emoji.name;
        return name.replace(/_/g, ' ');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cursor.emojiId]);

    const areSearchResultsEmpty = filter.length !== 0 && categoryOrEmojisRows.length === 1 && categoryOrEmojisRows?.[0]?.items?.[0]?.categoryName === SEARCH_RESULTS;

    const EmojiRow = useMemo<ComponentType<ListChildComponentProps<CategoryOrEmojiRow[]>>>(() => ({
        index,
        style,
        data,
    }) => (
        <EmojiPickerCategoryOrEmojiRow
            index={index}
            style={style}
            data={data}
            onEmojiClick={handleEmojiClick}
            onEmojiMouseOver={handleEmojiOnMouseOver}
        />
    ), [handleEmojiOnMouseOver, handleEmojiClick]);

    return (
        <div
            className='emoji-picker__inner'
            role='application'
            data-testid='EmojiPicker'
        >
            <div
                aria-live='assertive'
                className='sr-only'
            >
                <FormattedMessage
                    id='emoji_picker_item.emoji_aria_label'
                    defaultMessage='{emojiName} emoji'
                    values={{
                        emojiName: cursorEmojiName,
                    }}
                />
            </div>
            <div className='emoji-picker__search-container'>
                <EmojiPickerSearch
                    ref={searchInputRef}
                    value={filter}
                    cursorCategoryIndex={cursorCategoryIndex}
                    cursorEmojiIndex={cursorEmojiIndex}
                    focus={focusOnSearchInput}
                    onEnter={handleEnterOnEmoji}
                    onChange={handleFilterChange}
                    onKeyDown={handleKeyboardEmojiNavigation}
                    resetCursorPosition={resetCursor}
                />
                <EmojiPickerSkin
                    userSkinTone={userSkinTone}
                    onSkinSelected={setUserSkinTone}
                />
            </div>
            <EmojiPickerCategories
                isFiltering={filter.length > 0}
                active={activeCategory}
                categories={categories}
                onClick={handleCategoryClick}
                onKeyDown={handleKeyboardEmojiNavigation}
                focusOnSearchInput={focusOnSearchInput}
            />
            {areSearchResultsEmpty ? (
                <NoResultsIndicator
                    variant={NoResultsVariant.ChannelSearch}
                    titleValues={{channelName: `"${filter}"`}}
                />
            ) : (
                <CursorContext.Provider value={cursor}>
                    <EmojiPickerCurrentResults
                        ref={infiniteLoaderRef}
                        isFiltering={filter.length > 0}
                        activeCategory={activeCategory}
                        categoryOrEmojisRows={categoryOrEmojisRows}
                        setActiveCategory={setActiveCategory}
                        getCustomEmojis={getCustomEmojis}
                        customEmojiPage={customEmojiPage}
                        incrementEmojiPickerPage={incrementEmojiPickerPage}
                        customEmojisEnabled={customEmojisEnabled}
                        EmojiRow={EmojiRow}
                    />
                </CursorContext.Provider>
            )}
            <div className='emoji-picker__footer'>
                <EmojiPickerPreview
                    emoji={cursor.emoji}
                />
                <EmojiPickerCustomEmojiButton
                    currentTeamName={currentTeamName}
                    customEmojisEnabled={customEmojisEnabled}
                />
            </div>
        </div>
    );
};

export default memo(EmojiPicker);
